import {useCallback, useMemo} from "react";
import {matchPath, useLocation, useNavigate} from "react-router-dom";
import {routes} from "../../../utils/routes";

/** Вкладки панели машины в порядке MyTabs — имена в URL, индексы только здесь. */
export const CAR_TABS = ["chat", "history", "dids", "dtc"] as const;
export type CarTab = (typeof CAR_TABS)[number];

export const tabIndex = (tab: CarTab): number => CAR_TABS.indexOf(tab);
export const tabByIndex = (i: number): CarTab => CAR_TABS[i] ?? "chat";

/** Неизвестный или пустой `?tab=` — «Обсуждение». */
const parseTab = (raw: string | null): CarTab => (CAR_TABS as readonly string[]).includes(raw ?? "") ? raw as CarTab : "chat";

export interface IOpenCar {
    tab?: CarTab;
    /** Лента к непрочитанному / вниз (S12.2): одноразово, в `location.state`, ссылка не прокручивает. */
    atChat?: boolean;
}

/** Состояние навигации доски: `atChat` — открыть машину у ленты. */
export interface ICarLinkState {
    atChat?: boolean;
}

/**
 * Машина и вкладка панели — в URL (S15): `/machines?car=<id>&tab=<…>`.
 * Клики пишут только в URL, стор заполняет эффект BoardPage. `tab=chat` —
 * умолчание, в URL не пишется. Работает с любой страницы: путь — доска;
 * уже на доске — `replace`, с другой страницы — обычный переход («назад» вернёт).
 * В Стате (S17.2) панель своя: `car=`/`tab=` пишутся в URL Статы (`replace`).
 */
export function useCarLink() {
    const navigate = useNavigate();
    const location = useLocation();
    const here = [routes.machines, routes.stats].find((p) => !!matchPath(p, location.pathname));
    const pathname = here ?? routes.machines;
    const search = here ? location.search : "";

    const go = useCallback(
        (params: URLSearchParams, state?: ICarLinkState) => {
            const q = params.toString();
            navigate({pathname, search: q ? `?${q}` : ""}, {replace: !!here, state});
        },
        [navigate, pathname, here],
    );

    const openCar = useCallback(
        (id: string, {tab, atChat}: IOpenCar = {}) => {
            const params = new URLSearchParams(search);
            params.set("car", id);
            const next = atChat ? "chat" : tab ?? "chat";
            if (next === "chat") params.delete("tab");
            else params.set("tab", next);
            go(params, atChat ? {atChat: true} : undefined);
        },
        [go, search],
    );

    const closeCar = useCallback(() => {
        const params = new URLSearchParams(search);
        params.delete("car");
        params.delete("tab");
        go(params);
    }, [go, search]);

    const setTab = useCallback(
        (tab: CarTab) => {
            const params = new URLSearchParams(search);
            if (tab === "chat") params.delete("tab");
            else params.set("tab", tab);
            go(params);
        },
        [go, search],
    );

    return {openCar, closeCar, setTab};
}

/** Открытая машина из URL (`?car=`) или null. */
export function useCarParam(): string | null {
    const {search} = useLocation();
    return useMemo(() => new URLSearchParams(search).get("car"), [search]);
}

/** Вкладка панели из URL с clamp'ом к известным. */
export function useCarTab(): CarTab {
    const {search} = useLocation();
    return useMemo(() => parseTab(new URLSearchParams(search).get("tab")), [search]);
}
